import { AddIcon, EditIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  Spacer,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { MetriportMedicalApi, Patient } from "@metriport/api-sdk";
import { useContext, useState } from "react";
import { BsPeopleFill } from "react-icons/bs";
import { FaFileMedical, FaFire } from "react-icons/fa";
import { MedicalAppStateContext } from "../../contexts/medical";
import { Actions, Analytics, Features } from "../../shared/analytics";
import { Button } from "../../shared/Button";
import PatientDocuments from "../patient-documents";
import { FHIRResources } from "../fhir-resources";
import PatientForm from "./form";

export default function Patients({ api }: { api: MetriportMedicalApi }) {
  const bgColor = useColorModeValue("white", "gray.700");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [patientIdForDocuments, setPatientIdForDocuments] = useState<string | undefined>(undefined);
  const [patientIdForFHIRResources, setPatientIdForFHIRResources] = useState<string | undefined>(
    undefined
  );
  const [patientToEdit, setPatientToEdit] = useState<string | undefined>(undefined);
  const { medicalState } = useContext(MedicalAppStateContext);

  const selectedFacility = medicalState.selectedFacility;
  if (!selectedFacility) {
    throw new Error("Programming error: no facility has been selected");
  }

  function onEdit(patient: Patient): void {
    Analytics.emit(Actions.open, Features.form, { type: Features.patient });
    setPatientToEdit(patient.id);
    onOpen();
  }

  function onAdd(): void {
    Analytics.emit(Actions.open, Features.form, { type: Features.patient });
    setPatientToEdit(undefined);
    onOpen();
  }

  function onOpenDocuments(patient: Patient): void {
    Analytics.emit(Actions.open, Features.form, { type: Features.document });
    setPatientIdForDocuments(patient.id);
  }

  function onCloseDocuments(): void {
    Analytics.emit(Actions.close, Features.form, { type: Features.document });
    setPatientIdForDocuments(undefined);
  }

  function onOpenFHIRResources(patient: Patient): void {
    Analytics.emit(Actions.open, Features.form, { type: Features.fhir });
    setPatientIdForFHIRResources(patient.id);
  }

  function onCloseFHIRResources(): void {
    Analytics.emit(Actions.close, Features.form, { type: Features.fhir });
    setPatientIdForFHIRResources(undefined);
  }

  const closeForm = () => {
    Analytics.emit(Actions.close, Features.form, { type: Features.patient });
    onClose();
  };

  const patients = medicalState.store?.patients;

  return (
    <Stack spacing={4} w={"max-content"} bg={bgColor} rounded={"xl"} boxShadow={"lg"} p={6} my={12}>
      <Flex minWidth={"max-content"} alignItems="center" gap={"2"}>
        <HStack>
          <Icon as={BsPeopleFill} />
          <Heading as="h4" size="md">
            Patients at {selectedFacility.name}
          </Heading>
        </HStack>
        <Spacer />
        <Button onClick={onAdd} leftIcon={<AddIcon />}>
          Add
        </Button>
      </Flex>
      <Box>
        <TableContainer>
          <Table variant="striped">
            <Thead>
              <Tr>
                <Th>ID</Th>
                <Th>First</Th>
                <Th>Last</Th>
                <Th>DOB</Th>
              </Tr>
            </Thead>
            <Tbody>
              {patients && selectedFacility.id ? (
                Object.keys(patients).map((id, i) => {
                  const patient = patients[id];
                  if (patient.facilityIds.includes(selectedFacility.id)) {
                    return (
                      <PatientRow
                        key={i}
                        patient={patient}
                        onEdit={onEdit}
                        onOpenDocuments={onOpenDocuments}
                        onOpenFHIRResources={onOpenFHIRResources}
                      />
                    );
                  }
                })
              ) : (
                <></>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      <PatientForm patientId={patientToEdit} api={api} isOpen={isOpen} onClose={closeForm} />

      {/* Document Management */}
      {!!patientIdForDocuments && (
        <PatientDocuments
          api={api}
          patientId={patientIdForDocuments}
          facilityId={selectedFacility.id}
          placement="right"
          onClose={onCloseDocuments}
          size={"xl"}
        />
      )}

      {/* FHIR Data Explorer */}
      {!!patientIdForFHIRResources && (
        <FHIRResources
          api={api}
          patientId={patientIdForFHIRResources}
          placement="right"
          onClose={onCloseFHIRResources}
          size={"xl"}
        />
      )}
    </Stack>
  );
}

const PatientRow = ({
  patient,
  onEdit,
  onOpenDocuments,
  onOpenFHIRResources,
}: {
  patient: Patient;
  onEdit: (patient: Patient) => void;
  onOpenDocuments: (patient: Patient) => void;
  onOpenFHIRResources: (patient: Patient) => void;
}) => {
  return (
    <Tr>
      <Td className="ph-no-capture">{patient.id}</Td>
      <Td className="ph-no-capture">{patient.firstName}</Td>
      <Td className="ph-no-capture">{patient.lastName}</Td>
      <Td className="ph-no-capture">{patient.dob}</Td>
      <Td>
        <Button onClick={() => onEdit(patient)} leftIcon={<EditIcon />}>
          Edit
        </Button>
      </Td>
      <Td>
        <Button onClick={() => onOpenDocuments(patient)} leftIcon={<FaFileMedical />}>
          Documents
        </Button>
      </Td>
      <Td>
        <Button onClick={() => onOpenFHIRResources(patient)} leftIcon={<FaFire />}>
          FHIR Resources
        </Button>
      </Td>
    </Tr>
  );
};
